import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FC, ReactElement } from "react";
import styled from "styled-components";
import { faCheck, faCopy, faLock, faTimes } from "@fortawesome/pro-light-svg-icons";
import React from "react";

interface CookieConsentSettingsProps {
    necessary: boolean;
    preferences: boolean;
    statistics: boolean;
    marketing: boolean;
    unclassified: boolean;
    date: string;
    token: string;
    change(): void;
    onClose(): void;
}

type Props = {
    date: { title: string; date: string };
    id: { title: string; id: string };
    changeBtn: string;
    title: string;
    toggles: Record<string, { title: string; state: string; required: boolean }>;
    onChange(): void;
    onClose(): void;
};

const Container = styled.div`
  font-family: "Poppins", sans-serif;
  background-color: #ffffff;
  border-radius: 8px;
  width: 456px;
  position: fixed;
  z-index: 99999;
  bottom: 24px;
  left: 24px;
  box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.15);
  @media screen and (max-width: 500px) {
    width: 90%;
  }
`;

const Header = styled.div`
  padding: 26px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 18px;
  color: #323330;
  line-height: 21px;
  font-family: var(--branding-font), sans-serif;
  font-weight: 700;
`;

const Content = styled.div`
  padding: 24px;
`;

const Flex = styled.div`
  color: #323330;
  font-size: 18px;
  line-height: 21px;
`;

const Divider = styled.div`
  background-color: #e0e3dd;
  width: 100%;
  height: 1px;
`;

const Button = styled.button`
  width: calc(100% - 48px);
  margin: 24px;
  color: #ffffff;
  font-size: 18px;
  line-height: 21px;
  font-weight: 600;
  text-align: center;
  background-color: #323330;
  padding: 16px 24px;
  border: none;
  font-family: var(--branding-font), sans-serif;
  border-radius: 8px;
  cursor: pointer;
  transition: 0.25s background-color;
  &:hover {
    background-color: #2b2b29;
  }
`;

const Item = styled.div`
  font-size: 14px;
  line-height: 24px;
  font-weight: 400;
  color: #7cc242;
`;

const ItemText = styled.span`
  margin-left: 10px;
  color: #323330;
`;

const ContentDivider = styled.div`
  background-color: #e0e3dd;
  height: 1px;
  width: 100%;
  margin: 16px 0;
`;
const CloseIcon = styled(FontAwesomeIcon)`
  cursor: pointer;
  color: #323330;
  transition: 0.25s color;
  &:hover {
    color: #7cc242;
  }
`;
const Icon = styled(FontAwesomeIcon)`
  width: 14px !important;
  font-weight: initial;
  line-height: initial;
`;

const CookieIcon = styled.span`
  font-weight: 400;
`;

const CookieConsentSettings: FC<Props> = ({ onChange, changeBtn, date, id, onClose, title, toggles }) => {
    return (
        <Container>
            <Header>
                <Flex>{title}</Flex>
                <CloseIcon onClick={onClose} icon={faTimes} />
            </Header>
            <Divider />
            <Content>
                {Object.keys(toggles).map((t) => {
                    const item = toggles[t];
                    return (
                        <Item key={t}>
                            <Icon icon={item.required ? faLock : (item.state == "granted") ? faCheck : faTimes} />
                            <ItemText>{item.title}</ItemText>
                        </Item>
                    );
                })}
                <ContentDivider />
                <TitleValueItem title={date.title} value={date.date} />
                <TitleValueItem title={id.title} value={id.id} overflow />
            </Content>
            <Divider />
            <Button onClick={onChange}>{changeBtn}</Button>
        </Container>
    );
};

interface TitleValueItemProps {
    title: string;
    value: string;
    overflow?: boolean;
}

const ItemContainer = styled.div`
	&:not(:last-child) {
		margin-bottom: 8px;
	}
`;

const ItemTitle = styled.div`
  color: #626560;
  font-size: 12px;
  line-height: 20px;
  font-weight: 400;
`;

const ItemValue = styled.div<{ overflow?: boolean }>`
  font-weight: 600;
  color: #626560;
  font-size: 12px;
  line-height: 20px;
  ${({ overflow }) =>
          overflow
                  ? `
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
  `
                  : ""}
`;

const CopyIcon = styled(FontAwesomeIcon)`
  cursor: pointer;
  color: #323330;
  transition: 0.25s color;
  font-size: 12px;
  margin-left: 12px;
  &:hover {
    color: #7cc242;
  }
`;

const TitleValueItem = (props: TitleValueItemProps): ReactElement => {
    return (
        <ItemContainer>
            <ItemTitle>
                {props.title}
                {props.overflow && <CopyIcon onClick={() => navigator.clipboard.writeText(props.value)} icon={faCopy} />}
            </ItemTitle>
            <ItemValue overflow={props.overflow}>{props.value}</ItemValue>
        </ItemContainer>
    );
};

export default CookieConsentSettings;
